import React, { useState, useEffect } from 'react';
import { Modal, Button, ListGroup, Form, Card, Row, Col, Spinner } from 'react-bootstrap';

const API_URL = process.env.REACT_APP_API_URL;

const emptyQualification = { university: '', course: '', year: '' };
const emptyExperience = { company: '', role: '', from: '', to: '' };

const ConsultantTab = () => {
  const [consultants, setConsultants] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    skills: "",
    position: "",
    rate: "",
    availability: "",
    qualification: [ {...emptyQualification} ],
    experience: [ {...emptyExperience} ],
    projects: "",
    cv: null
  });

  useEffect(() => {
    fetchConsultants();
  }, []);

  const fetchConsultants = async () => {
    const res = await fetch(`${API_URL}/api/admin/consultants`);
    const data = await res.json();
    setConsultants(data);
  };

  const handleShowDetail = async (consultant) => {
    setLoadingDetail(true);
    setShowDetail(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/consultants/${consultant._id}`);
      const data = await res.json();
      setSelected(data);
    } catch (err) {
      setSelected(consultant); // fallback to basic data
    }
    setLoadingDetail(false);
  };

  const handleDelete = async () => {
    if (!selected?._id) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/consultants/${selected._id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setShowDeleteConfirm(false);
        setShowDetail(false);
        setSelected(null);
        fetchConsultants();
      }
    } finally {
      setDeleting(false);
    }
  };

  // === ADD CONSULTANT LOGIC ===

  // For dynamic qualification and experience fields
  const handleQualChange = (i, field, value) => {
    setForm(f => ({
      ...f,
      qualification: f.qualification.map((q, idx) => idx === i ? {...q, [field]: value } : q)
    }));
  };
  const addQual = () => setForm(f => ({ ...f, qualification: [...f.qualification, {...emptyQualification}] }));
  const removeQual = (i) => setForm(f => ({
    ...f,
    qualification: f.qualification.length > 1 ? f.qualification.filter((_, idx) => idx !== i) : f.qualification
  }));

  const handleExpChange = (i, field, value) => {
    setForm(f => ({
      ...f,
      experience: f.experience.map((e, idx) => idx === i ? {...e, [field]: value } : e)
    }));
  };
  const addExp = () => setForm(f => ({ ...f, experience: [...f.experience, {...emptyExperience}] }));
  const removeExp = (i) => setForm(f => ({
    ...f,
    experience: f.experience.length > 1 ? f.experience.filter((_, idx) => idx !== i) : f.experience
  }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);

    let formData = new FormData();
    // Prepare projects
    let projectsArr = [];
    if (form.projects) {
      try { projectsArr = JSON.parse(form.projects); }
      catch {
        projectsArr = form.projects.split(',').map(x => x.trim()).filter(Boolean);
      }
    }
    // Prepare fields
    formData.append("name", form.name);
    formData.append("email", form.email);
    formData.append("skills", form.skills);
    formData.append("position", form.position);
    formData.append("rate", form.rate);
    formData.append("availability", form.availability);
    formData.append("qualification", JSON.stringify(form.qualification.filter(q => q.university || q.course || q.year)));
    formData.append("experience", JSON.stringify(form.experience.filter(e => e.company || e.role || e.from || e.to)));
    formData.append("projects", JSON.stringify(projectsArr));
    if (form.cv) formData.append("cv", form.cv);

    try {
      const res = await fetch(`${API_URL}/api/admin/consultants`, {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        setShowCreate(false);
        setForm({
          name: "",
          email: "",
          skills: "",
          position: "",
          rate: "",
          availability: "",
          qualification: [ {...emptyQualification} ],
          experience: [ {...emptyExperience} ],
          projects: "",
          cv: null
        });
        fetchConsultants();
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <h4>Consultants</h4>
        </Col>
        <Col className="text-end">
          <Button onClick={() => setShowCreate(true)}>Add Consultant</Button>
        </Col>
      </Row>

      <ListGroup>
        {consultants.map(c => (
          <ListGroup.Item key={c._id} action onClick={() => handleShowDetail(c)}>
            <b>{c.name}</b> <span className="text-muted">({c.position || c.skills})</span>
          </ListGroup.Item>
        ))}
      </ListGroup>

      {/* Create Consultant Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)}>
        <Form onSubmit={handleCreate}>
          <Modal.Header closeButton>
            <Modal.Title>Create Consultant</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>Name</Form.Label>
              <Form.Control required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Email</Form.Label>
              <Form.Control required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Skills</Form.Label>
              <Form.Control required value={form.skills} onChange={e => setForm(f => ({ ...f, skills: e.target.value }))} placeholder="Comma separated" />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Position</Form.Label>
              <Form.Control value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Rate</Form.Label>
              <Form.Control value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Availability</Form.Label>
              <Form.Control value={form.availability} onChange={e => setForm(f => ({ ...f, availability: e.target.value }))} />
            </Form.Group>
            
            <Form.Label className="mt-2">Qualification(s)</Form.Label>
            {form.qualification.map((q, i) => (
              <Row className="mb-2" key={i}>
                <Col>
                  <Form.Control placeholder="University" value={q.university} onChange={e => handleQualChange(i, "university", e.target.value)} />
                </Col>
                <Col>
                  <Form.Control placeholder="Course" value={q.course} onChange={e => handleQualChange(i, "course", e.target.value)} />
                </Col>
                <Col>
                  <Form.Control placeholder="Year" value={q.year} onChange={e => handleQualChange(i, "year", e.target.value)} />
                </Col>
                <Col xs="auto">
                  <Button variant="outline-danger" size="sm" onClick={() => removeQual(i)} disabled={form.qualification.length === 1}>-</Button>
                  <Button variant="outline-success" size="sm" className="ms-1" onClick={addQual}>+</Button>
                </Col>
              </Row>
            ))}

            <Form.Label className="mt-2">Experience(s)</Form.Label>
            {form.experience.map((e, i) => (
              <Row className="mb-2" key={i}>
                <Col>
                  <Form.Control placeholder="Company" value={e.company} onChange={ev => handleExpChange(i, "company", ev.target.value)} />
                </Col>
                <Col>
                  <Form.Control placeholder="Role" value={e.role} onChange={ev => handleExpChange(i, "role", ev.target.value)} />
                </Col>
                <Col>
                  <Form.Control placeholder="From" value={e.from} onChange={ev => handleExpChange(i, "from", ev.target.value)} />
                </Col>
                <Col>
                  <Form.Control placeholder="To" value={e.to} onChange={ev => handleExpChange(i, "to", ev.target.value)} />
                </Col>
                <Col xs="auto">
                  <Button variant="outline-danger" size="sm" onClick={() => removeExp(i)} disabled={form.experience.length === 1}>-</Button>
                  <Button variant="outline-success" size="sm" className="ms-1" onClick={addExp}>+</Button>
                </Col>
              </Row>
            ))}

            <Form.Group className="mb-2 mt-3">
              <Form.Label>Projects (comma separated or JSON array)</Form.Label>
              <Form.Control value={form.projects} onChange={e => setForm(f => ({ ...f, projects: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>CV (PDF)</Form.Label>
              <Form.Control type="file" accept="application/pdf" onChange={e => setForm(f => ({ ...f, cv: e.target.files[0] }))} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={creating}>
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal show={showDetail} onHide={() => setShowDetail(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Consultant Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetail ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : selected && (
            <div>
              <p><b>Name:</b> {selected.name}</p>
              <p><b>Email:</b> {selected.email}</p>
              <p><b>Skills:</b> {selected.skills}</p>
              <p><b>Position:</b> {selected.position}</p>
              <p><b>Rate:</b> {selected.rate}</p>
              <p><b>Availability:</b> {selected.availability}</p>
              <p><b>Projects:</b> {(selected.projects || []).join(', ')}</p>
              <h6>Qualifications</h6>
              <ul>
                {(selected.qualification || []).map((q, i) => (
                  <li key={i}>{q.course} from {q.university} ({q.year})</li>
                ))}
              </ul>
              <h6>Experience</h6>
              <ul>
                {(selected.experience || []).map((e, i) => (
                  <li key={i}>{e.role} at {e.company} ({e.from} - {e.to})</li>
                ))}
              </ul>
              {selected.cv && selected.cv.data && (
                <>
                  <h6>CV Preview</h6>
                  <embed
                    src={`data:${selected.cv.contentType};base64,${selected.cv.data}`}
                    type="application/pdf"
                    width="100%"
                    height="400px"
                    allowFullScreen
                  />
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
          >
            Delete Consultant
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE CONFIRMATION MODAL */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this consultant?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Yes, Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ConsultantTab;
